import api from "./api";
import dayjs from "dayjs";
import { getCheckoutClientInfo, removeTokens } from "./authService";
import iq_api from "./iqApi";
import { getKsCompanyDetails } from "./accIqService";
import APP_CONFIG from "../config/appConfig";

export const getNameInitials = (name) => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0].toUpperCase();
  const lastInitial = words[words.length - 1][0].toUpperCase();

  return firstInitial + lastInitial;
};

export const replaceUnderSquarebySpace = (word) => {
  return word
    ?.replace(/[_\[\]]/g, " ") // Replace underscores and square brackets with space
    .replace(/\s+/g, " ") // Normalize multiple spaces to single space
    .trim() // Remove leading/trailing spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

// Function to truncate given description upto given length
export const truncateDescription = (description, upToLength) => {
  try {
    let response = [];
    let descriptionLimit = upToLength;
    let isTruncated = description?.length > descriptionLimit;
    let truncatedDescription = isTruncated
      ? `${description.slice(0, descriptionLimit)} `
      : description;
    response["isTruncated"] = isTruncated;
    response["truncated_info"] = truncatedDescription;
    return response;
  } catch (error) {
    console.log(error);
    console.error("Unable to truncate:", error);
    throw error;
  }
};

export const removeMarketAtEnd = (str) => {
  const trimmedStr = str.trim();

  const marketWord = "market";

  const regex = new RegExp(`${marketWord}$`, "i");

  // If the string ends with "market", remove it
  if (regex.test(trimmedStr)) {
    return trimmedStr.slice(0, -marketWord.length).trim();
  }

  return trimmedStr;
};

export const formatValueInBillions = (value) => {
  let billionValue = parseFloat(value) / 1000; // Convert from million to billion

  if (billionValue >= 10) {
    return parseFloat(Math.round(billionValue)); // No decimal places
  } else if (billionValue >= 1 && billionValue < 10) {
    return parseFloat(billionValue.toFixed(1)); // 1 decimal place
  } else if (billionValue < 1) {
    return parseFloat(billionValue.toFixed(1)); // 1 decimal place
  }
};

export const smartRevenueDisplay = (amountInMillion) => {
  let amount = parseFloat(amountInMillion); // Amount is in millions
  let suffix = " Mn"; // Default suffix

  // If the amount is greater than 999 million, convert to billions
  if (amount > 999) {
    amount = amount / 1000; // Convert to billions
    suffix = " Bn";
  }

  // Handling rounding and decimal places based on the amount
  if (amount >= 10) {
    // Show 1 decimal place for amounts >= 10
    amount = (Math.round(amount * 10) / 10).toFixed(1);
  } else if (amount >= 1) {
    // Show 1 decimal place if the value is less than 10 but more than or equal to 1
    amount = amount.toFixed(1);
  } else if (amount) {
    // Keep 1 decimal place for values less than 1
    amount = amount.toFixed(1);
  }
  if (amount) {
    return amount + suffix;
  } else {
    return "";
  }
};

/*
export const formatDateForRecentDevelopments = (dateString) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};
*/

// For browser compatible dates
export const formatDateForRecentDevelopments = (dateString) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Parse the ISO 8601 date string
  const date = new Date(dateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return ""; // If invalid date, return empty string
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

/*
export const displayFormattedDate = (date_string = "", formatString, show_time_ago = false) => {

  if (date_string.includes('ago')) {
    return date_string;
  }
  
  date_string = date_string.trim();
  date_string = date_string.substring(0, 10); // Nudges somewhere published_date : "2024-10-22 & 2024-10-29". was expected kind of 2024-10-22T00:00:00
  if(!date_string){
    return "";
  }
  
  if(show_time_ago === true){
    return timeAgo(date_string);
  }

  const date = dayjs(date_string); // Parse the ISO date string
  switch (formatString) {
    case 'YYYY-MM-DD':
      return date.format('YYYY-MM-DD');
    case 'DD-MM-YYYY':
      return date.format('DD-MM-YYYY');
    case 'MMM, YYYY':
      return date.format('MMM, YYYY');
    default:
      return date_string; // Return the original string if the format is unknown
  }
};
*/

export const displayFormattedDate = (
  date_string = "",
  formatString,
  show_time_ago = false
) => {
  if (!date_string) return ""; // Handle empty values

  if (date_string.includes("ago")) {
    return date_string; // If already in "time ago" format, return as is
  }

  // Adjust regex to match both DD-MM-YYYY and YYYY-MM-DD formats
  const extractedDate =
    date_string.match(/(\d{2}-\d{2}-\d{4}|\d{4}-\d{2}-\d{2})/)?.[0] || "";

  if (!extractedDate) return date_string; // If no valid date found, return original

  // Safari-compatible date parsing
  const formattedDateStr = extractedDate.replace(/-/g, "/");
  const date = dayjs(new Date(formattedDateStr));

  if (!date.isValid()) return extractedDate; // Return original if invalid

  if (show_time_ago) {
    return timeAgo(extractedDate); // Ensure correct format for timeAgo
  }

  switch (formatString) {
    case "YYYY-MM-DD":
      return date.format("YYYY-MM-DD");
    case "DD-MM-YYYY":
      return date.format("DD-MM-YYYY");
    case "DD-MMM-YYYY":
      return date.format("DD-MMM-YYYY");
    case "MMM, YYYY":
      return date.format("MMM, YYYY");
    default:
      return extractedDate; // Return the extracted date if format is unknown
  }
};

export const formatRelevanceScore = (relevance_score) => {
  if (isNaN(relevance_score)) {
    return relevance_score;
  }
  return Math.round(relevance_score);
};

export const displayPlusNumberNearedRounding = (number, base = 10) => {
  if (number >= base) {
    const roundedDown = Math.floor(number / base) * base;
    return `${roundedDown}+`;
  }
  return number.toString();
};

export const timeAgo = (inputDate) => {
  if (!inputDate) return "";

  // Convert inputDate to a Safari-compatible format
  const formattedDateStr = inputDate.replace(/-/g, "/");
  const date = new Date(formattedDateStr);

  if (isNaN(date)) return inputDate; // Return original if invalid

  const now = new Date();
  const diffTime = now - date;

  // Convert to seconds, minutes, hours, and days
  const diffSeconds = Math.floor(diffTime / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays < 1) return "Today";
  if (diffDays === 1) return "Y'day";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return "1w ago";
  if (diffDays < 21) return "2w ago";
  if (diffDays < 28) return "3w ago";
  if (diffDays <= 30) return "4w ago";

  // Return in "MMM'YY" format if more than or equals to 30 days
  return date
    .toLocaleDateString("en-US", { month: "short", year: "2-digit" })
    .replace(" ", "'");
};

export const smartDecimalNumberFormatter = (number) => {
  number = parseFloat(number);

  return Math.abs(number % 1) < 0.01 ? number.toFixed(0) : number.toFixed(2);
};

export const numberWithCommas = (x) => {
  if (x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else {
    return x;
  }
};

export const ensureProtocol = (url) => {
  if (url && !/^https?:\/\//i.test(url)) {
    return "https://" + url;
  }
  return url;
};

export const stripUrl = (url) => {
  return url.replace(/^(https?:\/\/)?(www\.)?/, "");
};

/*** Start: Remove Error Screen ***/
export const removeWebpackErrorOverlay = () => {
  const overlayDiv = document.getElementById(
    "webpack-dev-server-client-overlay-div"
  );
  const overlayBackdrop = document.getElementById(
    "webpack-dev-server-client-overlay"
  );
  if (overlayDiv) {
    fadeOut(overlayDiv);
  }
  if (overlayBackdrop) {
    fadeOut(overlayBackdrop);
  }
};
// Helper function to animate fade-out
function fadeOut(element) {
  element.style.transition = "opacity 0.5s ease"; // Adjust time for a slower or faster fade-out
  element.style.opacity = 1; // Ensure the element is fully visible initially
  setTimeout(() => {
    element.style.opacity = 0; // Start fade-out
  }, 0);
  setTimeout(() => {
    element.style.display = "none"; // Remove from the display after fading out
  }, 500); // This should match the duration in the transition (0.5s)
}
/*** End: Remove Error Screen ***/

export const setRecordAsFavourite = async (favObj) => {
  let api_url = "";
  if (favObj.record_type === "account") {
    api_url = "/bookmarks/add_bookmark_account";
  } else if (favObj.record_type === "competitor") {
    api_url = "/bookmarks/add_bookmark_competitor";
  } else if (favObj.record_type === "growth_opportunity") {
    api_url = "/bookmarks/add_bookmark_growth_opportunity";
  } else if (favObj.record_type === "contact") {
    api_url = "/bookmarks/add_bookmark_contact";
  }

  if (api_url) {
    try {
      const response = await api.post(api_url, favObj);
      if (response.data.status === 201 || response.data.status === 200) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Failed to Mark Records as Favourite:", error);
      throw error;
    }
  }
};

export const getMyFavouritesByType = async (record_type) => {
  const checkoutClientInfo = getCheckoutClientInfo();

  let favObj = {};
  favObj.type = record_type;
  favObj.client_id = checkoutClientInfo?.checkoutClientId || "";

  let api_url = "/bookmarks/get_bookmark_list";

  /*
  if (record_type === 'account' 
    || record_type === 'report'
    || record_type === 'competitor'
    || record_type === 'all'
  ){

  }
  */

  if (api_url) {
    try {
      const response = await api.get(api_url, { params: favObj });
      if (response.data.status === 201 || response.data.status === 200) {
        return response?.data?.result?.list || [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Failed to Fetch Bookmarked Records:", error);
      throw error;
    }
  }
};

export const getUrlDetailsObj = (location, urlParams) => {
  const locationObj = location();
  const urlParamsObj = urlParams();

  const page = locationObj.pathname.split("/")[1];
  // const page_fullPath_params = locationObj.pathname.substring(1).split("/").slice(1);

  const page_params = Object.keys(urlParamsObj).reduce((acc, key) => {
    acc[key] = decodeURIComponent(urlParamsObj[key]);
    return acc;
  }, {});

  const fullPath = locationObj.pathname.substring(1);

  const searchParams = new URLSearchParams(locationObj.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  // Combine the page and dynamic params into an object
  const pageParsedData = {
    page,
    page_params,
    query_params: queryParams,
  };

  return pageParsedData;
};

export const getUserStatsObject = (
  type = "",
  action = "",
  data = "",
  page = "",
  account_id = ""
) => {
  let userStat = {
    type: type,
    action: action,
    data: data,
    page: page,
    account_id: account_id,
  };
  return userStat;
};

export const getTabType = (activeParentTabKey = "") => {
  var tab_type = "";
  if (activeParentTabKey == "overview") {
    tab_type = "Overview";
  } else if (activeParentTabKey == "business") {
    tab_type = "Business";
  } else if (activeParentTabKey == "growth") {
    tab_type = "Growth Opportunities";
  } else if (activeParentTabKey == "nudges_and_events") {
    tab_type = "Nudges & Events";
  } else if (activeParentTabKey == "questions") {
    tab_type = "Prompters";
  } else if (activeParentTabKey == "contacts") {
    tab_type = "Contacts";
  } else if (activeParentTabKey == "competitor_insights") {
    tab_type = "Competitor Insights";
  } else if (activeParentTabKey == "technology") {
    tab_type = "Tech Landscape";
  } else if (activeParentTabKey == "financial") {
    tab_type = "Financials";
  } else if (activeParentTabKey == "documents") {
    tab_type = "Documents";
  }
  return tab_type;
};

export const recordActivity = async (userStat = {}) => {
  try {
    let recordUserStatsUrl = "/user_stats/save_stats";

    if (recordUserStatsUrl) {
      try {
        const response = await api.post(recordUserStatsUrl, userStat);
        if (response.status === 201 || response.status === 200) {
          return response?.result?.message || [];
        } else {
          return [];
        }
      } catch (error) {
        console.error("Failed to save user stats:", error);
        throw error;
      }
    }
  } catch (error) {
    console.log(error);
    console.error("Unable to add user stats:", error);
    throw error;
  }
};

export const setReviewSectionEntity = async (reviewObj) => {
  let api_url = "/likes/add_like";

  try {
    const response = await api.post(api_url, reviewObj);
    if (response.data.status === 201 || response.data.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Failed to Update Review:", error);
    throw error;
  }
};

export const setFavouriteSectionEntity = async (reviewObj) => {
  let api_url = "/likes/update_favourite";

  try {
    const response = await api.post(api_url, reviewObj);
    if (response.data.status === 201 || response.data.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Failed to Update Review:", error);
    throw error;
  }
};

export const fetchReviewSectionEntityList = async (
  accountName,
  clientName,
  section_name = "",
  entity_code = ""
) => {
  let api_url = "/likes/get_like_list";

  const reqReviewObj = {
    account_name: accountName,
    client_name: clientName,
    section: section_name || "",
    entity_code: entity_code || "",
  };

  try {
    const response = await api.get(api_url, { params: reqReviewObj });
    if (response.data.status === 201 || response.data.status === 200) {
      return response?.data?.result?.list?.likes || [];
    } else {
      return [];
    }
  } catch (error) {
    console.error("Failed to Fetch Review:", error);
    throw error;
  }
};

export const getMyLastViewedAccount = async () => {
  let api_url = "/accounts/last_viewed_account";

  if (api_url) {
    try {
      const response = await api.get(api_url);
      if (response.data.status === 201 || response.data.status === 200) {
        return response?.data?.result?.list || [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Failed to Fetch Bookmarked Records:", error);
      throw error;
    }
  }
};

export const getDomainAndClientWiseAccounts = async (search_string) => {
  let api_url = "/accounts/get_domain_and_client_wise_accounts";

  if (api_url) {
    try {
      const response = await api.get(api_url, {
        params: { search_string: decodeURIComponent(search_string) },
      });
      if (response.data.status === 201 || response.data.status === 200) {
        return response?.data?.result?.list || [];
      } else {
        return [];
      }
    } catch (error) {
      console.error(
        "Failed to Fetch getDomainAndClientWiseAccounts Records:",
        error
      );
      throw error;
    }
  }
};

export const getApiFieldValue = (action = "") => {
  var param = { field: "", value: "" };
  switch (action) {
    case "delete":
      param.field = "is_delete";
      param.value = 1;
      break;
    case "undo_delete":
      param.field = "is_delete";
      param.value = 0;
      break;
    case "status_on":
      param.field = "status";
      param.value = 1;
      break;
    case "status_off":
      param.field = "status";
      param.value = 0;
      break;
    case "activate":
      param.field = "is_active";
      param.value = 1;
      break;
    case "deactivate":
      param.field = "is_active";
      param.value = 0;
      break;
    case "is_avail_for_demo_on":
      param.field = "is_avail_for_demo";
      param.value = 1;
      break;
    case "is_avail_for_demo_off":
      param.field = "is_avail_for_demo";
      param.value = 0;
      break;
    case "verified":
      param.field = "is_verified";
      param.value = 1;
      break;
    case "not_verified":
      param.field = "is_verified";
      param.value = 0;
      break;
    case "":
      param.field = "";
      param.value = "";
      break;
    default:
      param.field = "";
      param.value = "";
      break;
  }
  return param;
};

export const fetchEventsData = async (params) => {
  try {
    const response = await api.post("/events/get_events", params);
    return response;
  } catch (error) {
    console.error("Failed to Fetch fetchEventsData: ", error);
    throw error;
  }
};

export const fetchCompanyCompareData = async ({
  page_name,
  company1_code,
  company2_code,
  reports_for,
}) => {
  try {
    const response = await api.post("/company_profiles/get_compare_data", {
      page_name,
      company1_code,
      company2_code,
      reports_for,
    });
    return response;
  } catch (error) {
    console.error("Failed to fetchCompanyCompareData: ", error);
    throw error;
  }
};

export const getCompetitors = async (company_name) => {
  try {
    const response = await api.post("/company_profiles/get_competitors", {
      company_name,
    });
    return response;
  } catch (error) {
    console.error("Failed to getCompetitors: ", error);
    throw error;
  }
};

export const getAccountDetails = async (params) => {
  try {
    const response = await api.get("/accounts/get_accounts", {
      params,
    });
    return response;
  } catch (error) {
    console.error("Failed to getAccountDetails: ", error);
    throw error;
  }
};

export const fetchDynamicTabsData = async (
  clientName = "",
  accountName = ""
) => {
  try {
    const response = await iq_api.post("/chatgpt/get_coustom_tab", {
      client: clientName,
      account: accountName,
    });

    return response?.data?.records || [];
  } catch (error) {
    console.error("Failed to dynamic tabs: ", error);
    throw error;
  }
};

export const transformResultsToTableData = (results = []) => {
  return results
    .filter((item) => item?.response?.fields) // Ensure fields exist
    .map((item) => {
      const { fields } = item.response;
      const flatEntry = {};

      // Use parent-level company name always
      flatEntry.competitor = item.company || item.competitor || "Unknown";

      Object.entries(fields).forEach(([key, obj]) => {
        if (key !== "company" && key !== "competitor") {
          const fieldData = {
            value: obj?.value || "",
            source: obj?.source || "",
            data_type: obj?.data_type || "",
            confidence_score: obj?.confidence_score ?? null,
            reasoning: obj?.reasoning || "",
            data_type_classification: obj?.data_type_classification || "",
            publication_date: obj?.publication_date || "",
          };

          // Populate value + 3 types of insights with metadata included
          flatEntry[key] = fieldData;
          flatEntry[`${key}_positive_insight`] = fieldData;
          flatEntry[`${key}_negative_insight`] = fieldData;
          flatEntry[`${key}_recent_development`] = fieldData;
        }
      });

      return flatEntry;
    });
};

export const transformResultsToCompProfileData = (results = []) => {
  const tableData = [];

  const oldArrays = [
    "primary_markets",
    "sub_markets",
    "application_markets",
    "market_segments",
    "customer_segments",
    "strategic_initiatives",
    "technology_segments",
    "geographic_segments",
    "application_segments",
    "gtm_model",
    "distribution_network",
    "channel_mix",
    "material_categories",
    "cost_categories",
    "distribution_channels",
    "performance_metrics",
    "supply_chain_components",
    "geography_segments",
  ];

  // helper to read a property from item or item.response
  const readProp = (item, key) => {
    if (!item) return undefined;
    if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];
    if (
      item.response &&
      Object.prototype.hasOwnProperty.call(item.response, key)
    )
      return item.response[key];
    return undefined;
  };

  // helper to build the standard field object
  const buildFieldDataFromObj = (obj) => ({
    value: obj?.value ?? obj?.name ?? "",
    source: obj?.source ?? "",
    data_type: obj?.data_type ?? "",
    confidence_score:
      obj?.confidence_score !== undefined ? obj.confidence_score : null,
    reasoning: obj?.reasoning ?? "",
    publication_date: obj?.publication_date ?? "",
    data_type_classification: obj?.data_type_classification ?? "",
  });

  results.forEach((rawItem) => {
    const item = rawItem || {};
    const competitor =
      readProp(item, "target_company") ||
      readProp(item, "company") ||
      readProp(item, "company_name") ||
      "Unknown";

    // ----- CASE 1: OLD ARRAY-BASED FORMAT -----
    const oldArrayKey = oldArrays.find((k) => Array.isArray(readProp(item, k)));
    if (oldArrayKey) {
      const arr = readProp(item, oldArrayKey) || [];
      arr.forEach((segment) => {
        const flatEntry = { competitor };

        const factorName =
          segment?.competitive_factor ||
          segment?.subdimension_name ||
          readProp(item, "subdimension_name") ||
          readProp(item, "dimension") ||
          "";

        flatEntry.competitive_factor = factorName;
        flatEntry.competitive_factor_name = factorName;

        // preserve name if present on segment
        if (segment?.name) flatEntry.name = segment.name;

        Object.entries(segment || {}).forEach(([key, value]) => {
          if (value && typeof value === "object") {
            // metric object with 'value' OR an object like launch_x (has name + metadata)
            const fieldData = buildFieldDataFromObj(value);
            flatEntry[key] = fieldData;

            // clone insight variations for compatibility
            flatEntry[`${key}_positive_insight`] = fieldData;
            flatEntry[`${key}_negative_insight`] = fieldData;
            flatEntry[`${key}_recent_development`] = fieldData;

            // expose primitive sub-properties (like category/year) as separate keys
            Object.entries(value).forEach(([subK, subV]) => {
              if (
                subV !== null &&
                (typeof subV === "string" ||
                  typeof subV === "number" ||
                  typeof subV === "boolean")
              ) {
                // e.g., launch_1_category
                flatEntry[`${key}_${subK}`] = subV;
              }
            });
          } else {
            // primitive
            flatEntry[key] = value;
          }
        });

        tableData.push(flatEntry);
      });

      // finished with this item
      return;
    }

    // ----- CASE 2: NEW / FACTOR-BASED / FIELDS FORMAT -----
    // Collect candidate object keys from both item and item.response
    const combinedKeys = new Set([
      ...Object.keys(item || {}),
      ...(item.response ? Object.keys(item.response || {}) : []),
    ]);

    // build map key -> value (preferring top-level item over response)
    const keyValueMap = {};
    combinedKeys.forEach((k) => {
      keyValueMap[k] = Object.prototype.hasOwnProperty.call(item, k)
        ? item[k]
        : item.response?.[k];
    });

    // prefer explicit container names
    let factorsContainerKey =
      Object.keys(keyValueMap).find((k) => k === "competitive_factors") ||
      Object.keys(keyValueMap).find((k) => k === "technology_capabilities") ||
      Object.keys(keyValueMap).find((k) => k === "value_drivers") ||
      Object.keys(keyValueMap).find((k) => k === "risk_categories") ||
      Object.keys(keyValueMap).find((k) => k === "components") ||
      Object.keys(keyValueMap).find((k) => k === "partnerships") ||
      Object.keys(keyValueMap).find((k) => k === "channels") ||
      Object.keys(keyValueMap).find((k) => k === "materials") ||
      Object.keys(keyValueMap).find((k) => k === "capex_trends") ||
      Object.keys(keyValueMap).find((k) => k === "suppliers") ||
      Object.keys(keyValueMap).find((k) => k === "inbound_modes") ||
      Object.keys(keyValueMap).find((k) => k === "ports") ||
      Object.keys(keyValueMap).find((k) => k === "warehouses") ||
      Object.keys(keyValueMap).find((k) => k === "channel_partners") ||
      Object.keys(keyValueMap).find((k) => k === "outbound_modes") ||
      Object.keys(keyValueMap).find((k) => k === "export_channels") ||
      Object.keys(keyValueMap).find((k) => k === "import_exposure") ||
      Object.keys(keyValueMap).find((k) => k === "sustainability") ||
      Object.keys(keyValueMap).find((k) => k === "finance") ||
      Object.keys(keyValueMap).find((k) => k === "innovation") ||
      Object.keys(keyValueMap).find((k) => k === "risks") ||
      Object.keys(keyValueMap).find((k) => k === "fields");

    // Special handling for gtm_model.components
    if (!factorsContainerKey && keyValueMap.gtm_model?.components) {
      keyValueMap.__gtm_components = keyValueMap.gtm_model.components;
      factorsContainerKey = "__gtm_components";
    }

    // Special handling for distribution_network.partnerships
    if (
      !factorsContainerKey &&
      keyValueMap.distribution_network?.partnerships
    ) {
      keyValueMap.__distribution_partnerships =
        keyValueMap.distribution_network.partnerships;
      factorsContainerKey = "__distribution_partnerships";
    }

    // Special handling for channel_mix.channels
    if (!factorsContainerKey && keyValueMap.channel_mix?.channels) {
      keyValueMap.__channel_channels = keyValueMap.channel_mix.channels;
      factorsContainerKey = "__channel_channels";
    }

    // fallback: find a container whose children look like factor keys (factor_*, launch_*)
    if (!factorsContainerKey) {
      factorsContainerKey = Object.keys(keyValueMap).find((k) =>
        Object.keys(keyValueMap[k] || {}).some((subk) =>
          /^(factor_|factor|launch_|launch|patent_|patent|parameter|paramater_1|driver_|driver|category_|category|theme_|theme | component_ | component | partnership_ | partnership | channel_ | channel )/i.test(
            subk
          )
        )
      );
    }

    // final fallback: find a container whose values look like objects with 'value' or 'name'
    if (!factorsContainerKey) {
      factorsContainerKey = Object.keys(keyValueMap).find((k) =>
        Object.values(keyValueMap[k] || {}).some(
          (v) =>
            v &&
            typeof v === "object" &&
            (Object.prototype.hasOwnProperty.call(v, "value") ||
              Object.prototype.hasOwnProperty.call(v, "name") ||
              // Object.prototype.hasOwnProperty.call(v, "title") ||
              Object.prototype.hasOwnProperty.call(v, "competitive_factor"))
        )
      );
    }

    if (factorsContainerKey) {
      const factorsContainer = keyValueMap[factorsContainerKey] || {};

      // Object.entries(factorsContainer).forEach(([factorKey, factorData]) => {
      //   // if factorData itself is not an object (unlikely), skip
      //   if (!factorData || typeof factorData !== "object") return;

      //   const flatEntry = { competitor };

      //   const factorName =
      //     factorData?.competitive_factor ||
      //     factorData?.subdimension_name ||
      //     factorData?.name ||
      //     readProp(item, "subdimension_name") ||
      //     factorKey ||
      //     readProp(item, "dimension") ||
      //     "";

      //   flatEntry.competitive_factor = factorName;
      //   flatEntry.competitive_factor_name = factorName;

      //   // preserve name if present (e.g., launch object)
      //   if (factorData?.name) flatEntry.name = factorData.name;

      //   Object.entries(factorData || {}).forEach(([metricKey, metricData]) => {
      //     // skip metadata keys that are not metric objects
      //     if (
      //       metricKey === "competitive_factor" ||
      //       metricKey === "name" ||
      //       metricKey === "subdimension_name"
      //     )
      //       return;

      //     if (metricData && typeof metricData === "object") {
      //       // If metricData has a proper 'value' field -> treat normally
      //       if (Object.prototype.hasOwnProperty.call(metricData, "value")) {
      //         const fieldData = buildFieldDataFromObj(metricData);
      //         flatEntry[metricKey] = fieldData;
      //         flatEntry[`${metricKey}_positive_insight`] = fieldData;
      //         flatEntry[`${metricKey}_negative_insight`] = fieldData;
      //         flatEntry[`${metricKey}_recent_development`] = fieldData;

      //         // also expose other primitive subproperties (if any)
      //         Object.entries(metricData).forEach(([subK, subV]) => {
      //           if (
      //             subV !== null &&
      //             (typeof subV === "string" ||
      //               typeof subV === "number" ||
      //               typeof subV === "boolean")
      //           ) {
      //             flatEntry[`${metricKey}_${subK}`] = subV;
      //           }
      //         });
      //       } else if (
      //         Object.prototype.hasOwnProperty.call(metricData, "name")
      //       ) {
      //         // e.g., launches: use 'name' as the primary value
      //         const fieldData = buildFieldDataFromObj(metricData);
      //         flatEntry[metricKey] = fieldData;
      //         flatEntry[`${metricKey}_positive_insight`] = fieldData;
      //         flatEntry[`${metricKey}_negative_insight`] = fieldData;
      //         flatEntry[`${metricKey}_recent_development`] = fieldData;

      //         // put the human-readable name onto the root (if not present or different)
      //         if (!flatEntry.name) flatEntry.name = metricData.name;

      //         // expose primitive sub-properties like category/year as separate keys
      //         Object.entries(metricData).forEach(([subK, subV]) => {
      //           if (
      //             subV !== null &&
      //             (typeof subV === "string" ||
      //               typeof subV === "number" ||
      //               typeof subV === "boolean")
      //           ) {
      //             flatEntry[`${metricKey}_${subK}`] = subV;
      //           }
      //         });
      //       } else {
      //         // fallback: convert object to a standard fieldData using JSON string as value
      //         const fieldData = {
      //           value: JSON.stringify(metricData),
      //           source: metricData?.source ?? "",
      //           data_type: metricData?.data_type ?? "",
      //           confidence_score:
      //             metricData?.confidence_score !== undefined
      //               ? metricData.confidence_score
      //               : null,
      //           reasoning: metricData?.reasoning ?? "",
      //           publication_date: metricData?.publication_date ?? "",
      //           data_type_classification:
      //             metricData?.data_type_classification ?? "",
      //         };
      //         flatEntry[metricKey] = fieldData;
      //         flatEntry[`${metricKey}_positive_insight`] = fieldData;
      //         flatEntry[`${metricKey}_negative_insight`] = fieldData;
      //         flatEntry[`${metricKey}_recent_development`] = fieldData;
      //       }
      //     } else {
      //       // primitive value (string/number)
      //       flatEntry[metricKey] = metricData;
      //     }
      //   });

      //   tableData.push(flatEntry);
      // });

      if (Array.isArray(factorsContainer)) {
        Object.entries(factorsContainer).forEach(([factorKey, factorData]) => {
          // if factorData itself is not an object (unlikely), skip
          if (!factorData || typeof factorData !== "object") return;

          const flatEntry = { competitor };

          const factorName =
            factorData?.competitive_factor ||
            factorData?.subdimension_name ||
            factorData?.name ||
            readProp(item, "subdimension_name") ||
            factorKey ||
            readProp(item, "dimension") ||
            "";

          flatEntry.competitive_factor = factorName;
          flatEntry.competitive_factor_name = factorName;

          // preserve name if present (e.g., launch object)
          if (factorData?.name) flatEntry.name = factorData.name;

          Object.entries(factorData || {}).forEach(
            ([metricKey, metricData]) => {
              // skip metadata keys that are not metric objects
              if (
                metricKey === "competitive_factor" ||
                metricKey === "name" ||
                metricKey === "subdimension_name"
              )
                return;

              if (metricData && typeof metricData === "object") {
                // If metricData has a proper 'value' field -> treat normally
                if (Object.prototype.hasOwnProperty.call(metricData, "value")) {
                  const fieldData = buildFieldDataFromObj(metricData);
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;

                  // also expose other primitive subproperties (if any)
                  Object.entries(metricData).forEach(([subK, subV]) => {
                    if (
                      subV !== null &&
                      (typeof subV === "string" ||
                        typeof subV === "number" ||
                        typeof subV === "boolean")
                    ) {
                      flatEntry[`${metricKey}_${subK}`] = subV;
                    }
                  });
                } else if (
                  Object.prototype.hasOwnProperty.call(metricData, "name")
                ) {
                  // e.g., launches: use 'name' as the primary value
                  const fieldData = buildFieldDataFromObj(metricData);
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;

                  // put the human-readable name onto the root (if not present or different)
                  if (!flatEntry.name) flatEntry.name = metricData.name;

                  // expose primitive sub-properties like category/year as separate keys
                  Object.entries(metricData).forEach(([subK, subV]) => {
                    if (
                      subV !== null &&
                      (typeof subV === "string" ||
                        typeof subV === "number" ||
                        typeof subV === "boolean")
                    ) {
                      flatEntry[`${metricKey}_${subK}`] = subV;
                    }
                  });
                } else {
                  // fallback: convert object to a standard fieldData using JSON string as value
                  let fieldData = {};
                  if (Array.isArray(metricData) && metricData?.length > 0) {
                    const joinedValues = metricData
                      .map((item) => item.value || item.name || item || "")
                      .join(", ");

                    fieldData = {
                      value: joinedValues,
                      source: metricData[0]?.source ?? "", // Assuming 'source' is the same across all objects
                      data_type: metricData[0]?.data_type ?? "", // Assuming 'data_type' is the same across all objects
                      confidence_score:
                        metricData[0]?.confidence_score !== undefined
                          ? metricData[0].confidence_score
                          : null,
                      reasoning: metricData[0]?.reasoning ?? "", // Assuming 'reasoning' is the same across all objects
                      publication_date: metricData[0]?.publication_date ?? "", // Assuming the same for publication_date
                      data_type_classification:
                        metricData[0]?.data_type_classification ?? "", // Assuming the same for data_type_classification
                    };
                  } else {
                    fieldData = {
                      value: JSON.stringify(metricData),
                      source: metricData?.source ?? "",
                      data_type: metricData?.data_type ?? "",
                      confidence_score:
                        metricData?.confidence_score !== undefined
                          ? metricData.confidence_score
                          : null,
                      reasoning: metricData?.reasoning ?? "",
                      publication_date: metricData?.publication_date ?? "",
                      data_type_classification:
                        metricData?.data_type_classification ?? "",
                    };
                  }
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;
                }
              } else {
                // primitive value (string/number)
                flatEntry[metricKey] = metricData;
              }
            }
          );

          tableData.push(flatEntry);
        });
      } else if (
        typeof factorsContainer === "object" &&
        factorsContainer !== null
      ) {
        // If factorsContainer is a plain object, loop through its entries
        Object.entries(factorsContainer).forEach(([factorKey, factorData]) => {
          if (!factorData || typeof factorData !== "object") return;

          const flatEntry = { competitor };

          const factorName =
            factorData?.competitive_factor ||
            factorData?.subdimension_name ||
            factorData?.name ||
            readProp(item, "subdimension_name") ||
            factorKey ||
            readProp(item, "dimension") ||
            "";

          flatEntry.competitive_factor = factorName;
          flatEntry.competitive_factor_name = factorName;

          // preserve name if present (e.g., launch object)
          if (factorData?.name) flatEntry.name = factorData.name;

          Object.entries(factorData || {}).forEach(
            ([metricKey, metricData]) => {
              // skip metadata keys that are not metric objects
              if (
                metricKey === "competitive_factor" ||
                metricKey === "name" ||
                metricKey === "subdimension_name"
              )
                return;

              if (metricData && typeof metricData === "object") {
                // If metricData has a proper 'value' field -> treat normally
                if (Object.prototype.hasOwnProperty.call(metricData, "value")) {
                  const fieldData = buildFieldDataFromObj(metricData);
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;

                  // also expose other primitive subproperties (if any)
                  Object.entries(metricData).forEach(([subK, subV]) => {
                    if (
                      subV !== null &&
                      (typeof subV === "string" ||
                        typeof subV === "number" ||
                        typeof subV === "boolean")
                    ) {
                      flatEntry[`${metricKey}_${subK}`] = subV;
                    }
                  });
                } else if (
                  Object.prototype.hasOwnProperty.call(metricData, "name")
                ) {
                  // e.g., launches: use 'name' as the primary value
                  const fieldData = buildFieldDataFromObj(metricData);
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;

                  // put the human-readable name onto the root (if not present or different)
                  if (!flatEntry.name) flatEntry.name = metricData.name;

                  // expose primitive sub-properties like category/year as separate keys
                  Object.entries(metricData).forEach(([subK, subV]) => {
                    if (
                      subV !== null &&
                      (typeof subV === "string" ||
                        typeof subV === "number" ||
                        typeof subV === "boolean")
                    ) {
                      flatEntry[`${metricKey}_${subK}`] = subV;
                    }
                  });
                } else {
                  // fallback: convert object to a standard fieldData using JSON string as value
                  const fieldData = {
                    value: JSON.stringify(metricData),
                    source: metricData?.source ?? "",
                    data_type: metricData?.data_type ?? "",
                    confidence_score:
                      metricData?.confidence_score !== undefined
                        ? metricData.confidence_score
                        : null,
                    reasoning: metricData?.reasoning ?? "",
                    publication_date: metricData?.publication_date ?? "",
                    data_type_classification:
                      metricData?.data_type_classification ?? "",
                  };
                  flatEntry[metricKey] = fieldData;
                  flatEntry[`${metricKey}_positive_insight`] = fieldData;
                  flatEntry[`${metricKey}_negative_insight`] = fieldData;
                  flatEntry[`${metricKey}_recent_development`] = fieldData;
                }
              } else {
                // primitive value (string/number)
                flatEntry[metricKey] = metricData;
              }
            }
          );

          tableData.push(flatEntry);
        });
      } else {
        console.error(
          "factorsContainer should be either an object or an array of objects"
        );
      }
    }
  });

  return tableData;
};

// export const getAllSubdimensionMatches = (response, targetSubdimension) => {
//   if (!response?.results?.length) return [];

//   return response.results.filter(
//     (item) => item.subdimension_name === targetSubdimension
//   );
// };

// export const getAllSubdimensionMatches = (response, targetSubdimension) => {
//   if (!response?.results?.length) return [];

//   return response.results.filter(
//     (item) =>
//       item.response?.subdimension_name.toLowerCase() ===
//       targetSubdimension.toLowerCase()
//   );
// };

export const getAllSubdimensionMatches = (response, targetSubdimension) => {
  if (!response?.results?.length || !targetSubdimension) return [];

  return response.results.filter((item) => {
    const subdimensionName = item.response?.subdimension_name;
    return (
      typeof subdimensionName === "string" &&
      subdimensionName.toLowerCase() === targetSubdimension.toLowerCase()
    );
  });
};

export const getAllSubdimensionMatchesNewBenchmarking = (
  response,
  targetSubdimension
) => {
  if (!response?.results?.length || !targetSubdimension) return [];

  return response.results.filter((item) => {
    const subdimensionName = item?.subdimension_name;
    return (
      typeof subdimensionName === "string" &&
      subdimensionName.toLowerCase() === targetSubdimension.toLowerCase()
    );
  });
};

export const getAllDynamicSubdimensionMatches = (
  response,
  targetSubdimension = []
) => {
  if (!response?.results?.length || !Array.isArray(targetSubdimension))
    return [];

  const targetSet = new Set(
    targetSubdimension.map((name) => name?.toLowerCase())
  );

  return response.results.filter((item) => {
    const subdimensionName = item.response?.subdimension_name;
    const table = item.response?.table;

    return (
      typeof subdimensionName === "string" &&
      !targetSet.has(subdimensionName.toLowerCase()) &&
      table?.length > 0
    );
  });
};

// export const getAllPageInsights = (response) => {
//   if (!response?.results?.length)
//     return { positive: [], negative: [], sowhat: [] };

//   const insightEntry = response.results.find(
//     (item) =>
//       item.response &&
//       typeof item.response === "object" &&
//       "positive" in item.response &&
//       "negative" in item.response &&
//       "sowhat" in item.response
//   );

//   if (!insightEntry) {
//     return { positive: [], negative: [], sowhat: [] };
//   }

//   const { positive = [], negative = [], sowhat = [] } = insightEntry.response;

//   return { positive, negative, sowhat };
// };

export const getAllPageInsights = (response) => {
  if (!response?.results?.length) {
    return {
      positive: [],
      negative: [],
      sowhat: [],
      positives: {},
      negatives: {},
      sowhats: {},
    };
  }

  const insightEntry = response.results.find(
    (item) =>
      item.response &&
      typeof item.response === "object" &&
      "positive" in item.response &&
      "negative" in item.response &&
      "sowhat" in item.response
  );

  if (!insightEntry) {
    return {
      positive: [],
      negative: [],
      sowhat: [],
      positives: {},
      negatives: {},
      sowhats: {},
    };
  }

  const { positive = [], negative = [], sowhat = [] } = insightEntry.response;

  // detect new format (object with "value" field)
  const isNewFormat =
    Array.isArray(positive) &&
    positive.length > 0 &&
    typeof positive[0] === "object" &&
    "value" in positive[0];

  if (isNewFormat) {
    const mapArrayToKeyValue = (arr) =>
      arr.reduce((acc, { value, sources }) => {
        acc[value] = sources || "";
        return acc;
      }, {});

    return {
      positive: positive.map((p) => p.value),
      negative: negative.map((n) => n.value),
      sowhat: sowhat.map((s) => s.value),
      positives: mapArrayToKeyValue(positive),
      negatives: mapArrayToKeyValue(negative),
      sowhats: mapArrayToKeyValue(sowhat),
    };
  }

  // old format
  return {
    positive,
    negative,
    sowhat,
    positives: {},
    negatives: {},
    sowhats: {},
  };
};

export const getCompProfilePageInsights = (response) => {
  if (!response)
    return { positive: [], negative: [], sowhat: [], quant_snapshot: {} };

  // Case 1: Response itself has the arrays
  if (typeof response === "object") {
    const positive = Array.isArray(response.positive) ? response.positive : [];
    const negative = Array.isArray(response.negative) ? response.negative : [];
    const sowhat = Array.isArray(response.sowhat)
      ? response.sowhat
      : Array.isArray(response.so_what)
      ? response.so_what
      : [];

    if (positive.length || negative.length || sowhat.length) {
      return {
        positive,
        negative,
        sowhat,
        quant_snapshot: response.quant_snapshot || {},
      };
    }
  }

  // Case 2: Response has results array
  if (!response?.results?.length)
    return { positive: [], negative: [], sowhat: [], quant_snapshot: {} };

  const insightEntry = response.results.find((item) => {
    const res = item?.response;
    return (
      res &&
      typeof res === "object" &&
      (Array.isArray(res.positive) ||
        Array.isArray(res.negative) ||
        Array.isArray(res.sowhat) ||
        Array.isArray(res.so_what))
    );
  })?.response;

  if (!insightEntry) {
    return { positive: [], negative: [], sowhat: [], quant_snapshot: {} };
  }

  const {
    positive = [],
    negative = [],
    sowhat = [],
    so_what = [],
    quant_snapshot = {},
  } = insightEntry;

  return {
    positive,
    negative,
    sowhat: sowhat.length ? sowhat : so_what,
    quant_snapshot,
  };
};

export function formatBoldTextOlder(text) {
  if (!text || typeof text !== "string") return text ?? null; // return as is if not a string

  const parts = text.split(/(\*\*.*?\*\*)/g); // keep **...** parts in the array

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

export function formatBoldText(text) {
  if (!text || typeof text !== "string") return text ?? null;

  // 1) Normalize: force line breaks before headings/bullets/numbered items
  const normalized = text
    // break before #### / ### / ## / #
    .replace(/(?!^)\s+(####)\s/g, "\n$1 ")
    .replace(/(?!^)\s+(###)\s/g, "\n$1 ")
    .replace(/(?!^)\s+(##)\s/g, "\n$1 ")
    .replace(/(?!^)\s+(#)\s/g, "\n$1 ")
    // break before bullets "- "
    .replace(/(?!^)\s+-\s/g, "\n- ")
    // break before numbered list items "1. ", "2. ", ...
    .replace(/(?!^)\s+(\d+)\.\s/g, (m, d) => `\n${d}. `);

  const lines = normalized.split(/\n+/).filter(Boolean);

  // helper to keep your original **bold** behavior
  const renderInlineBold = (s, keyPrefix) => {
    const parts = s.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={`${keyPrefix}-b-${idx}`}>{part.slice(2, -2)}</strong>
        );
      }
      return <span key={`${keyPrefix}-t-${idx}`}>{part}</span>;
    });
  };

  return (
    <>
      {lines.map((line, i) => {
        // Headings
        if (/^####\s/.test(line)) {
          return (
            <span
              key={`h4-${i}`}
              style={{ display: "block", fontWeight: 700, margin: "6px 0 2px" }}
            >
              {renderInlineBold(line.replace(/^####\s/, ""), `h4-${i}`)}
            </span>
          );
        }
        if (/^###\s/.test(line)) {
          return (
            <span
              key={`h3-${i}`}
              style={{ display: "block", fontWeight: 700, margin: "6px 0 2px" }}
            >
              {renderInlineBold(line.replace(/^###\s/, ""), `h3-${i}`)}
            </span>
          );
        }
        if (/^##\s/.test(line)) {
          return (
            <span
              key={`h2-${i}`}
              style={{ display: "block", fontWeight: 700, margin: "6px 0 2px" }}
            >
              {renderInlineBold(line.replace(/^##\s/, ""), `h2-${i}`)}
            </span>
          );
        }
        if (/^#\s/.test(line)) {
          return (
            <span
              key={`h1-${i}`}
              style={{ display: "block", fontWeight: 700, margin: "6px 0 2px" }}
            >
              {renderInlineBold(line.replace(/^#\s/, ""), `h1-${i}`)}
            </span>
          );
        }

        // Bulleted list item
        if (/^-\s/.test(line)) {
          return (
            <span key={`li-${i}`} style={{ display: "block", marginLeft: 8 }}>
              • {renderInlineBold(line.replace(/^-+\s/, ""), `li-${i}`)}
            </span>
          );
        }

        // Numbered list item (keeps the "1. " prefix)
        // if (/^\d+\.\s/.test(line)) {
        //   const [, num, rest] = line.match(/^(\d+)\.\s(.*)$/) || [];
        //   return (
        //     <span key={`ol-${i}`} style={{ display: "block", marginLeft: 8 }}>
        //       <strong>{num}.</strong>{" "}
        //       {renderInlineBold(rest ?? line, `ol-${i}`)}
        //     </span>
        //   );
        // }

        // accept only 1 or 2 digit numbers as list markers (avoids years like 2028)
        const orderedListMatch = line.match(/^(\d{1,2})\.\s(.*)$/);
        if (orderedListMatch) {
          const [, num, rest] = orderedListMatch;
          return (
            <span key={`ol-${i}`} style={{ display: "block", marginLeft: 8 }}>
              <strong>{num}.</strong>{" "}
              {renderInlineBold(rest ?? line, `ol-${i}`)}
            </span>
          );
        }

        // Plain paragraph line (with bold inline)
        return (
          <span key={`p-${i}`}>
            {renderInlineBold(line, `p-${i}`)}
            {i < lines.length - 2 ? <br /> : null}
          </span>
        );
      })}
    </>
  );
}

export function formatStreamingHtmlResponse(rawText) {
  if (!rawText) return "";

  // Remove code block wrappers like ```html or ```
  const cleaned = rawText
    .replace(/```html/gi, "") // Remove ```html
    .replace(/```/g, "") // Remove ```
    .trim();

  return cleaned;
}

// export function getRnDSpendLineChartData(data) {
//   if (!Array.isArray(data?.results) || data.results.length === 0) return [];

//   // find the right record in results
//   const target = data.results.find(
//     (item) => item?.response?.subdimension_name === "R&D Spend Estimates"
//   )?.response;

//   if (!target?.fields?.annual_spending_data) return [];

//   const annualData = target.fields.annual_spending_data;

//   return Object.entries(annualData)
//     .map(([yearKey, yearData]) => {
//       const yearNum = parseInt(yearKey.replace("year_", ""), 10);

//       let numericAmount = null;
//       if (typeof yearData.amount === "string") {
//         const cleaned = yearData.amount
//           .replace(/\$/g, "")
//           .replace(/[^\d.]/g, "");
//         numericAmount = parseFloat(cleaned);
//         if (/M/i.test(yearData.amount)) {
//           numericAmount *= 1_000_000;
//         }
//       }

//       return {
//         year: yearNum.toString(), // string for chart category axis
//         spend: numericAmount / 1_000_000, // billions if your chart expects it
//         percent:
//           typeof yearData.percentage === "string"
//             ? parseFloat(yearData.percentage.replace("%", ""))
//             : Number(yearData.percentage),
//         source: yearData.source || "N/A",
//         reasoning: yearData.reasoning || "N/A",
//       };
//     })
//     .sort((a, b) => parseInt(a.year) - parseInt(b.year));
// }

export function getRnDSpendLineChartData(data) {
  if (!Array.isArray(data?.results) || data.results.length === 0) return [];

  const target = data.results.find(
    (item) => item?.response?.subdimension_name === "R&D Spend Estimates"
  )?.response;

  if (!target?.fields?.annual_spending_data) return [];

  const annualData = target.fields.annual_spending_data;

  return Object.entries(annualData)
    .map(([yearKey, yearData]) => {
      const yearNum = parseInt(yearKey.replace("year_", ""), 10);

      let spendInMillions = null;

      if (typeof yearData.amount === "string") {
        const raw = yearData.amount.replace(/\$/g, "").trim();
        const numeric = parseFloat(raw.replace(/[^\d.]/g, ""));

        if (/B/i.test(raw)) {
          spendInMillions = numeric * 1_000; // Billion → Million
        } else if (/M/i.test(raw)) {
          spendInMillions = numeric; // Already Million
        } else {
          spendInMillions = numeric; // Fallback → assume Million
        }
      } else if (typeof yearData.amount === "number") {
        spendInMillions = yearData.amount; // Already normalized
      }

      return {
        year: yearNum.toString(),
        spend: spendInMillions, //  ALWAYS MILLIONS
        percent:
          typeof yearData.percentage === "string"
            ? parseFloat(yearData.percentage.replace("%", ""))
            : Number(yearData.percentage),
        source: yearData.source || "N/A",
        reasoning: yearData.reasoning || "N/A",
      };
    })
    .filter((item) => Number.isFinite(item.spend))
    .sort((a, b) => parseInt(a.year) - parseInt(b.year));
}

export const transformPortfolioDataForInsightTable = (data) => {
  if (!data?.results) return [];

  const transformed = [];

  Object.values(data.results).forEach((category) => {
    const categoryName = category.category_name || "Unknown Category";

    Object.values(category.products || {}).forEach((product) => {
      transformed.push({
        category: categoryName, // filter chip
        product: {
          value: product.name,
          source: product.source,
          confidence_score: product.confidence_score,
          data_type: product.data_type,
          reasoning: product.reasoning,
        },
        description: {
          value: product.description,
          source: product.source,
          confidence_score: product.confidence_score,
          data_type: product.data_type,
          reasoning: product.reasoning,
        },
      });
    });
  });

  return transformed;
};

export const replaceMomentumWithSpan = (data = []) => {
  const formatMomentum = (value) => {
    if (value === "Rising") {
      return `<span style="color:green;font-weight:bold">${value}</span>`;
    }
    if (value === "Falling") {
      return `<span style="color:red;font-weight:bold">${value}</span>`;
    }
    if (value === "Stable") {
      return `<span style="color:orange;font-weight:bold">${value}</span>`;
    }
    return value || "—";
  };

  return data.map((item) => ({
    ...item,
    momentum: (
      <span
        dangerouslySetInnerHTML={{
          __html: formatMomentum(item.momentum),
        }}
      />
    ),
  }));
};

export const getCurrentSecionName = (sectionName = "") => {
  const sections = {
    "By Technology": "Market by Technology",
    "By Application": "Market by Application",
    "By Geography": "Market by Geography",
    "GTM Model": "GTM & Distribution Strategy Analysis",
    "Distribution Networks & Partnerships":
      "GTM & Distribution Strategy Analysis",
    "Channel Mix & Utilization": "GTM & Distribution Strategy Analysis",
  };

  return sections[sectionName] ?? sectionName;
};

export const getCustomRowIdentifierLabel = (identifierName = "") => {
  const identifiers = {
    gtm_component: "GTM Component",
  };

  return (
    identifiers[identifierName] ??
    identifierName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

{
  /*
// export const transformOrgChartData = (apiResponse) => {
//   if (!apiResponse[0]?.response?.fields) return null;

//   const fields = apiResponse[0].response.fields;
//   const leaders = Object.keys(fields).map((key, index) => ({
//     id: key,
//     name: fields[key].name,
//     title: fields[key].title,
//     reports_to: fields[key].reports_to,
//     level: fields[key].level,
//     functional_area: fields[key].functional_area,
//     location: fields[key].location,
//     confidence_score: fields[key].confidence_score,
//     source: fields[key].source,
//     publication_date: fields[key].publication_date,
//     reasoning: fields[key].reasoning,
//     children: [],
//   }));

//   // Build lookup
//   const byName = {};
//   leaders.forEach((leader) => {
//     byName[leader.name] = leader;
//   });

//   // Attach children to their parents
//   // let root = null;
//   // leaders.forEach((leader) => {
//   //   if (leader.reports_to && byName[leader.reports_to]) {
//   //     byName[leader.reports_to].children.push(leader);
//   //   } else {
//   //     // no valid parent, must be root
//   //     root = leader;
//   //   }
//   // });

//   let root = null;
//   let foundUnknownRoot = false;

//   leaders.forEach((leader) => {
//     // CASE 1: reports_to === "Unknown"
//     if (leader.reports_to === "Unknown") {
//       if (!foundUnknownRoot) {
//         // first Unknown → treat as root
//         root = leader;
//         foundUnknownRoot = true;
//       }
//       // skip ALL other Unknown entries
//       return;
//     }

//     // CASE 2: normal parent-child linking
//     if (leader.reports_to && byName[leader.reports_to]) {
//       byName[leader.reports_to].children.push(leader);
//     }
//   });

//   return root;
// }; */
}

export const transformOrgChartData = (apiResponse) => {
  if (!apiResponse?.[0]?.response?.fields) return null;

  const fields = apiResponse[0].response.fields;

  // Convert fields object → array
  const nodes = Object.keys(fields).map((key) => ({
    id: key,
    name: fields[key].name?.trim(),
    title: fields[key].title,
    reports_to: fields[key].reports_to?.trim(),
    level: fields[key].level,
    functional_area: fields[key].functional_area,
    location: fields[key].location,
    confidence_score: fields[key].confidence_score,
    source: fields[key].source,
    publication_date: fields[key].publication_date,
    reasoning: fields[key].reasoning,
    children: [],
  }));

  if (nodes.length === 0) return null;

  // First node is always root
  const root = nodes[0];

  // Build nameMap with normalized keys
  const normalize = (str) => (str ? str.trim().toLowerCase() : "");

  const nameMap = {};
  nameMap[normalize(root.name)] = root;

  // Register other nodes (skip Unknown)
  nodes.slice(1).forEach((node) => {
    if (normalize(node.reports_to) === "unknown") return;
    nameMap[normalize(node.name)] = node;
  });

  // Attach children
  nodes.slice(1).forEach((node) => {
    const childKey = normalize(node.name);
    const parentKey = normalize(node.reports_to);

    if (parentKey === "unknown") return;

    const parent = nameMap[parentKey];
    const child = nameMap[childKey];

    if (parent && child) {
      parent.children.push(child);
    }
  });

  return root;
};

// Function to check if specific columns meet multiple conditions
// export const checkColumns = (data, columns, conditions) => {
//   return data.every((row) => {
//     return columns.every((column) => {
//       const value = row[column];
//       const numericValue = parseFloat(value) || 0; // Ensure it's a number (0 if not)

//       // Apply each condition for the column
//       return conditions.every((condition) => {
//         if (condition === "lessThan1") {
//           return numericValue < 1;
//         } else if (condition === "notEqualToZero") {
//           return numericValue !== 0;
//         } else if (condition === "Data not available publicly") {
//           return numericValue === "Data not available publicly";
//         }
//         return true; // If no valid condition is provided
//       });
//     });
//   });
// };

export const checkColumns = (data, columnConditions) => {
  return data.every((row) => {
    return Object.entries(columnConditions).every(([column, conditions]) => {
      const value = row[column]?.value;

      // Ensure conditions is an array
      if (!Array.isArray(conditions)) {
        console.warn(`Conditions for column "${column}" must be an array`);
        return false;
      }

      return conditions.every((condition) => {
        switch (condition) {
          case "isNumber":
            return typeof value === "number" || !isNaN(parseFloat(value));
          case "lessThan1":
            return parseFloat(value) < 1;
          case "greaterThanZero":
            return parseFloat(value) > 0;
          case "notEqualToZero":
            return parseFloat(value) !== 0;
          case "isString":
            return typeof value === "string";
          case "notEqualToNA":
            return value !== "N/A" && value !== "Data not available publicly";
          default:
            return true;
        }
      });
    });
  });
};

// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const transformBaselineData = (apiData) => {
  // Helper function to get color based on category/type
  const getCategoryColor = (category) => {
    const colorMap = {
      "CASH COW": "#dc2626",
      "GROWTH ENGINE": "#059669",
      DIVERSIFICATION: "#059669",
      "STRATEGIC DEFENSE": "#6366f1",
      "PORTFOLIO FILL": "#6366f1",
      "INNOVATION SHOWCASE": "#059669",
      HIGH: "#ef4444",
      MEDIUM: "#f59e0b",
      LOW: "#10b981",
      OPTIMAL: "#10b981",
      STRATEGIC: "#10b981",
      MODERATE: "#f59e0b",
    };
    return colorMap[category] || "#6b7280";
  };

  // Helper to determine strategic category based on product data
  const getStrategicCategory = (product) => {
    if (!product) return "PORTFOLIO FILL";
    if (product.strategic_category) return product.strategic_category;
    const lifecycle = product.lifecycle_stage?.toLowerCase();
    const differentiators =
      product.key_differentiators?.join(" ").toLowerCase() || "";

    if (lifecycle === "mature" && differentiators.includes("validation")) {
      return "CASH COW";
    } else if (
      product.lifecycle_stage === "Growth" ||
      differentiators.includes("innovation")
    ) {
      return "GROWTH ENGINE";
    } else if (
      differentiators.includes("custom") ||
      differentiators.includes("service")
    ) {
      return "STRATEGIC DEFENSE";
    } else if (
      product.product_family?.includes("Multiplex") ||
      product.product_family?.includes("Single-Cell")
    ) {
      return "INNOVATION SHOWCASE → GROWTH ENGINE";
    }
    return "PORTFOLIO FILL";
  };

  // Transform products data
  const transformProducts = (products) => {
    if (!products || !Array.isArray(products)) {
      // Fallback data if products array is missing
      return [
        {
          line: "Chromatin Profiling (SimpleChIP, CUT&RUN, CUT&Tag)",
          category: "CASH COW (SimpleChIP) + GROWTH ENGINE (CUT&RUN/Tag)",
          bgColor: "#dc2626",
        },
      ];
    }

    return products.slice(0, 9).map((product) => ({
      line: product.product_family || "Product Line",
      category: getStrategicCategory(product),
      bgColor: getCategoryColor(getStrategicCategory(product)),
    }));
  };

  // Transform markets by technology
  const transformMarketsByTechnology = (markets) => {
    if (!markets || !Array.isArray(markets)) {
      return []; // Return empty array if no data
    }
    const revenuePercentages = ["58%", "12%", "6%", "8%", "7%", "6%", "3%"];

    // return markets.slice(0, 7).map((market, index) => ({
    return markets.map((market, index) => ({
      segment: market.technology_name || "Technology Segment",
      // percent: revenuePercentages[index] || "5%",
      percent:
        `${
          market.revenue_share_pct_estimate !== null &&
          market.revenue_share_pct_estimate !== undefined
            ? market.revenue_share_pct_estimate
            : ""
        }` || "",
    }));
  };

  // Transform markets by application
  const transformMarketsByApplication = (applications) => {
    if (!applications || !Array.isArray(applications)) {
      return []; // Return empty array if no data
    }

    return applications.map((app) => ({
      segment: app.application_name || "Application Segment",
      percent:
        `${
          app.revenue_share_pct_estimate !== null &&
          app.revenue_share_pct_estimate !== undefined
            ? app.revenue_share_pct_estimate
            : ""
        }` || "",
    }));
  };

  // Transform markets by geography
  const transformMarketsByGeography = (geographies) => {
    if (!geographies || !Array.isArray(geographies)) {
      return []; // Return empty array if no data
    }
    const revenuePercentages = ["44%", "28%", "25%", "1.5%", "1.2%", "0.3%"];

    // return geographies.slice(0, 6).map((geo, index) => ({
    return geographies.map((geo, index) => ({
      segment: `${geo.region} (${geo.go_to_market || "Mixed"} GTM) — ${
        index === 0
          ? "primary operating geography"
          : index === 1
          ? "secondary market focus"
          : index === 2
          ? "growth market"
          : index === 3
          ? "emerging market opportunity"
          : index === 4
          ? "niche market presence"
          : "minimal market presence"
      }`,
      // percent: revenuePercentages[index] || "1%",
      percent:
        `${
          geo.revenue_share_pct_estimate !== null &&
          geo.revenue_share_pct_estimate !== undefined
            ? geo.revenue_share_pct_estimate
            : ""
        }` || "",
    }));
  };

  // Transform manufacturing facilities
  const transformManufacturingFacilities = (facilities) => {
    if (!facilities || !Array.isArray(facilities)) {
      return []; // Return empty array if no data
    }

    const facilityTypes = {
      "Global Headquarters (Danvers Campus)": { icon: "🏢", risk: "MEDIUM" },
      "Beverly Manufacturing": { icon: "🧪", risk: "MEDIUM" },
      "Shanghai Office": { icon: "🌍", risk: "LOW" },
      "Netherlands Office": { icon: "🌍", risk: "LOW" },
    };

    return facilities.map((facility) => {
      const typeInfo = facilityTypes[facility.site_name] || {
        icon: "🏭",
        risk: "MEDIUM",
      };
      return {
        name: `${typeInfo.icon} ${facility.site_name}`,
        location: `${facility.location?.city || "Unknown"}, ${
          facility.location?.state_province || "Unknown"
        }, ${facility.location?.country || "Unknown"}`,
        // function: facility.capabilities?.join(", ") || "Operations",
        function: facility.function || "Operations",
        // riskLevel: typeInfo.risk,
        riskLevel: facility.risk_level,
        riskColor: getCategoryColor(typeInfo.risk),
      };
    });

    // return facilities.slice(0, 4).map((facility) => {
    //   const typeInfo = facilityTypes[facility.site_name] || {
    //     icon: "🏭",
    //     risk: "MEDIUM",
    //   };
    //   return {
    //     name: `${typeInfo.icon} ${facility.site_name}`,
    //     location: `${facility.location?.city || "Unknown"}, ${
    //       facility.location?.state_province || "Unknown"
    //     }, ${facility.location?.country || "Unknown"}`,
    //     // function: facility.capabilities?.join(", ") || "Operations",
    //     function: facility.function || "Operations",
    //     // riskLevel: typeInfo.risk,
    //     riskLevel: facility.risk_level,
    //     riskColor: getCategoryColor(typeInfo.risk),
    //   };
    // });
  };

  // Transform distribution channels
  const transformDistributionChannels = (channels) => {
    if (!channels || !Array.isArray(channels)) {
      return []; // Return empty array if no data
    }

    const channelIcons = {
      Online: "🌐",
      Distributor: "🏪",
      Partnership: "🤝",
    };

    const salesPercentages = ["65%", "25%", "10%"];
    const impactLevels = ["OPTIMAL", "MODERATE", "STRATEGIC"];

    return channels.slice(0, 3).map((channel, index) => ({
      type: `${channelIcons[channel.channel_type] || "📦"} ${
        channel.channel_type
      }`,
      percent: salesPercentages[index] || "10%",
      description: channel.description
        ? channel.description.split(".")[0] + "."
        : "Distribution channel",
      impact: impactLevels[index] || "MODERATE",
      impactColor: getCategoryColor(impactLevels[index]),
    }));
  };

  // Transform customer segments
  const transformCustomerSegments = (segments) => {
    if (!segments || !Array.isArray(segments)) {
      return []; // Return empty array if no data
    }

    const positionData = [
      { filled: 4, label: "Strong Position" },
      { filled: 4, label: "Strong Position" },
      { filled: 4, label: "Strong Position" },
      { filled: 2, label: "Moderate Position" },
    ];

    return segments.slice(0, 4).map((segment, index) => ({
      segment: segment.segment_name || "Customer Segment",
      position: {
        filledDots: positionData[index]?.filled || 3,
        totalDots: 5,
        label: positionData[index]?.label || "Good Position",
        dotColor: positionData[index]?.filled === 4 ? "#10b981" : "#f59e0b",
      },
      useCases:
        segment.purchase_drivers?.join("; ") ||
        "Research and development applications",
    }));
  };

  // Transform competitors
  // const transformCompetitors = (competitors) => {
  //   if (!competitors || !Array.isArray(competitors)) {
  //     return []; // Return empty array if no data
  //   }

  //   const competitorCategories = [
  //     { category: "🌐 Global Giants", threat: "HIGH" },
  //     { category: "🎯 Specialized Players", threat: "MEDIUM" },
  //     { category: "🚀 Emerging Competitors", threat: "LOW" },
  //   ];

  //   // If we have actual competitor data, use it
  //   if (competitors.length > 0) {
  //     const globalGiants = competitors
  //       .filter(
  //         (c) =>
  //           c.competitor_name?.includes("Abcam") ||
  //           c.competitor_name?.includes("Thermo Fisher") ||
  //           c.competitor_name?.includes("Bio-Rad") ||
  //           c.competitor_name?.includes("Merck")
  //       )
  //       .slice(0, 4);

  //     const specializedPlayers = competitors
  //       .filter(
  //         (c) =>
  //           c.competitor_name?.includes("BioLegend") ||
  //           c.competitor_name?.includes("Santa Cruz") ||
  //           c.competitor_name?.includes("Proteintech") ||
  //           c.competitor_name?.includes("Bio-Techne")
  //       )
  //       .slice(0, 4);

  //     const emergingCompetitors = competitors
  //       .filter(
  //         (c) =>
  //           c.competitor_name?.includes("Sino") ||
  //           c.competitor_name?.includes("Rockland") ||
  //           c.competitor_name?.includes("GenScript")
  //       )
  //       .slice(0, 3);

  //     const competitorGroups = [
  //       globalGiants,
  //       specializedPlayers,
  //       emergingCompetitors,
  //     ];

  //     return competitorCategories.map((category, index) => ({
  //       category: category.category,
  //       players:
  //         competitorGroups[index].map((c) => c.competitor_name).join(", ") ||
  //         "Various competitors",
  //       threatLevel: category.threat,
  //       threatColor: getCategoryColor(category.threat),
  //       advantage:
  //         index === 0
  //           ? "Scale, portfolio breadth, global distribution, acquisition power"
  //           : index === 1
  //           ? "Niche expertise, specialized technologies, focused customer segments"
  //           : "Cost advantage, regional presence, custom services",
  //     }));
  //   }

  //   // Fallback if no competitor data
  //   return competitorCategories.map((category, index) => ({
  //     category: category.category,
  //     players:
  //       index === 0
  //         ? "Abcam, Thermo Fisher Scientific, Bio-Rad, Merck KGaA"
  //         : index === 1
  //         ? "BioLegend, Santa Cruz Biotechnology, Proteintech, Bio-Techne"
  //         : "Sino Biological, Rockland Immunochemicals, GenScript",
  //     threatLevel: category.threat,
  //     threatColor: getCategoryColor(category.threat),
  //     advantage:
  //       index === 0
  //         ? "Scale, portfolio breadth, global distribution, acquisition power"
  //         : index === 1
  //         ? "Niche expertise, specialized technologies, focused customer segments"
  //         : "Cost advantage, regional presence, custom services",
  //   }));
  // };

  const transformCompetitors = (competitors) => {
    if (!competitors || !Array.isArray(competitors)) {
      return []; // Return empty array if no data
    }

    const competitorCategories = [
      { category: "🌐 Global Competitors", threat: "HIGH" },
      { category: "🎯 Regional Competitors", threat: "MEDIUM" },
      { category: "🚀 Emerging Competitors", threat: "LOW" },
    ];

    // If we have actual competitor data, use it
    if (competitors.length > 0) {
      const globalCompetitors = competitors
        .filter(
          (c) =>
            c.category_name === "Global Competitors" && c.key_players.length > 0
        )
        .map((category) =>
          category.key_players.map((player) =>
            typeof player === "object" ? player.name : player
          )
        );

      const regionalCompetitors = competitors
        .filter(
          (c) =>
            c.category_name === "Regional Competitors" &&
            c.key_players.length > 0
        )
        .map((category) =>
          category.key_players.map((player) =>
            typeof player === "object" ? player.name : player
          )
        );

      const emergingCompetitors = competitors
        .filter(
          (c) =>
            c.category_name === "Emerging Competitors" &&
            c.key_players.length > 0
        )
        .map((category) =>
          category.key_players.map((player) =>
            typeof player === "object" ? player.name : player
          )
        );

      const competitorGroups = [
        globalCompetitors.flat(),
        regionalCompetitors.flat(),
        emergingCompetitors.flat(),
      ];

      return competitors.map((category, index) => ({
        category:
          category.category_name === "Global Competitors"
            ? "🌐 Global Competitors"
            : category.category_name === "Regional Competitors"
            ? "🎯 Regional Competitors"
            : "🚀 Emerging Competitors",
        players:
          competitorGroups[index].length > 0
            ? competitorGroups[index].join(", ")
            : "Various competitors",
        threatLevel: category.threat_level || "UNKNOWN", // Dynamically fetched from data
        threatColor: getCategoryColor(category.threat_level),
        advantage: category.competitive_advantage || "N/A", // Dynamically fetched from data
      }));
    }

    // Fallback if no competitor data
    return competitorCategories.map((category, index) => ({
      category: category.category,
      players:
        index === 0
          ? "Henkel (Adhesive Technologies), H.B. Fuller, Bostik (Arkema), Sika, 3M (Industrial Adhesives & Tapes)"
          : index === 1
          ? "Franklin International (Titebond; Franklin Adhesives & Polymers), HarperLove (corrugated packaging adhesives), CP Adhesives (woodworking adhesives)"
          : "No emerging competitors identified.",
      threatLevel: category.threat,
      threatColor: getCategoryColor(category.threat),
      advantage:
        index === 0
          ? "Tailored Chemical emphasizes customization, vertical integration, and service-intensive accounts against scale competitors."
          : index === 1
          ? "Regional proximity, service breadth, and niche focus for sticky share."
          : "Market consolidation favors larger competitors; emerging challengers not clearly identified.",
    }));
  };

  // Main transformation - using the root level arrays
  return {
    companyInfo: {
      name: "📋 Company Baseline Profile",
      // overallConfidence: `${Math.round(
      //   (apiData.meta?.overall_confidence || 0.85) * 100
      // )}%`,
      overallConfidence: `${Math.round(
        apiData.meta?.overall_confidence || 0.85
      )}%`,
    },
    tabs: [
      { key: "products", label: "Products" },
      { key: "markets", label: "Markets" },
      { key: "operations", label: "Operations" },
      { key: "distribution", label: "Distribution" },
      { key: "customers", label: "Customers" },
      { key: "competition", label: "Competition" },
    ],
    products: transformProducts(apiData.products_and_solutions),
    markets: {
      technology: transformMarketsByTechnology(apiData.markets_by_technology),
      application: transformMarketsByApplication(
        apiData.markets_by_end_use_applications
      ),
      geography: transformMarketsByGeography(apiData.markets_by_geography),
    },
    operations: {
      facilities: transformManufacturingFacilities(
        apiData.manufacturing_facilities
      ),
      riskNote: {
        title: "Regional Manufacturing Concentration Risk",
        icon: "⚠️",
        content:
          "<strong>Northeast US Operations:</strong> Primary manufacturing concentrated in Massachusetts creates moderate supply chain vulnerability, but global office network provides distribution resilience...",
        bgColor: "#fef3c7",
        borderColor: "#f59e0b",
        titleColor: "#92400e",
      },
    },
    distribution: {
      channels: transformDistributionChannels(apiData.distribution_channels),
      analysis: {
        title: "Distribution Strategy Analysis",
        icon: "📊",
        content:
          "<strong>Direct Sales Advantage:</strong> 65% direct channel maintains high margins and customer relationships, supported by strong digital presence and customer loyalty...",
        bgColor: "#f0f9ff",
        borderColor: "#3b82f6",
        titleColor: "#1e40af",
      },
    },
    customers: transformCustomerSegments(apiData.markets_by_customer_segments),
    competition: {
      // competitors: transformCompetitors(apiData.competitors),
      competitors: transformCompetitors(
        apiData.competitive_landscape?.competitor_categories
      ),
      analysis: [
        {
          title: "Competitive Positioning Analysis",
          icon: "🎯",
          content:
            "<strong>Market Leadership:</strong> CST maintains co-leadership with Abcam in antibody citation share (~20%+ each). Key differentiators: validation rigor, signaling pathway expertise, and premium positioning vs broad portfolio competitors.",
          bgColor: "#f0f9ff",
          borderColor: "#3b82f6",
          titleColor: "#1e40af",
        },
        {
          title: "Competitive Threats",
          icon: "⚠️",
          content:
            "<strong>Scale & Acquisition Risk:</strong> Thermo Fisher ($40B+) and other giants can acquire specialized players and commoditize premium segments. Rising competition from Asian suppliers with cost advantages challenges traditional pricing models.",
          bgColor: "#fef2f2",
          borderColor: "#ef4444",
          titleColor: "#dc2626",
        },
      ],
    },
  };
};

// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

// helper to detect if a field is an object that can be used as tooltip payload
export const hasTooltipPayload = (field) =>
  field != null && typeof field === "object" && field.value != null;

export const getVendorCompanyId = (competitorInfo) => {
  if (competitorInfo?.vendor_company_id) {
    return competitorInfo.vendor_company_id;
  }

  try {
    const localData = JSON.parse(
      localStorage.getItem("localStorageCompetitorInfo")
    );
    return localData?.vendor_company_id || "";
  } catch {
    return "";
  }
};

export const getTaggedKsCompanies = (competitorInfo) => {
  if (competitorInfo?.ks_companies) {
    return competitorInfo.ks_companies;
  }

  try {
    const localData = JSON.parse(
      localStorage.getItem("localStorageCompetitorInfo")
    );
    return localData?.ks_companies || [];
  } catch {
    return "";
  }
};

export const getTaggedFirstKsCompany = (competitorInfo) => {
  if (competitorInfo?.ks_companies) {
    return competitorInfo.ks_companies[0];
  }

  try {
    const localData = JSON.parse(
      localStorage.getItem("localStorageCompetitorInfo")
    );
    return localData?.ks_companies[0] || "";
  } catch {
    return "";
  }
};

// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
export function encryptToken(token) {
  if (!token) return null;
  // Just return the token as-is, no encryption
  return token;
}

export function decryptToken(encryptedToken) {
  if (!encryptedToken) return null;
  // Just return the token as-is, no decryption
  return encryptedToken;
}

export function buildAuthenticatedReportUrl(baseUrl) {
  if (!baseUrl) {
    console.warn("No baseUrl provided");
    return baseUrl;
  }

  // const savedToken = localStorage.getItem("access_token");

  const encryptedToken = localStorage.getItem("access_token");

  if (!encryptedToken) {
    console.warn("No encrypted token available for report URL");
    return baseUrl;
  }

  const token = decryptToken(encryptedToken);
  if (!token) {
    console.warn("decryptToken returned null");
    return baseUrl;
  }

  // base64 encode, then URL encode
  const base64Token = btoa(token);
  const urlEncodedToken = encodeURIComponent(base64Token);

  // Append token to URL
  const separator = baseUrl.includes("?") ? "&" : "?";
  const finalUrl = `${baseUrl}${separator}token=${urlEncodedToken}`;

  return finalUrl;
}
// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const getKsCompanyName = async (ksCompanyCode = "") => {
  try {
    const res = await getKsCompanyDetails(ksCompanyCode);
    return res?.data?.data?.name || "";
  } catch (e) {
    return "";
  }
};

// export const routeBasedOnSubscription = (user, handleLogout) => {
//   const {
//     growth_iq,
//     growth_iq_role_name,
//     has_ask_growth_access,
//     has_comp_iq_access,
//   } = user || {};

//   // Check Growth IQ access
//   if (growth_iq !== 1) {
//     window.location.replace(
//       buildAuthenticatedReportUrl("https://www.mnmks.com")
//     );
//     removeTokens();
//     return;
//   }

//   // Super Admin / Account Manager
//   if (
//     growth_iq_role_name === "ROLE_MNM_SUBSCRIBER" ||
//     growth_iq_role_name === "ROLE_ACCOUNT_MANAGER"
//   ) {
//     window.location.replace("/watchboard");
//     return;
//   }

//   // Client Admin / Client Employee
//   if (
//     growth_iq_role_name === "ROLE_SUBSCRIBER_ADMIN" ||
//     growth_iq_role_name === "ROLE_SUBSCRIBER_EMPLOYEE"
//   ) {
//     if (user?.demo === APP_CONFIG?.market_iq_demo) {
//       window.location.replace("/markets");
//       return;
//     }

//     if (has_ask_growth_access === 1) {
//       window.location.replace("/watchboard");
//       return;
//     } else if (has_comp_iq_access === 1) {
//       window.location.replace("/clients_competitors");
//       return;
//     } else {
//       window.location.replace("/news");
//       return;
//     }
//   }
//   // No valid access → logout
//   handleLogout?.() ?? removeTokens();
// };

export const routeBasedOnSubscription = (user, handleLogout) => {
  if (!user) return "/login";

  const {
    growth_iq,
    growth_iq_role_name,
    has_ask_growth_access,
    has_comp_iq_access,
    salesplay,
    demo,
    user_growth_iq,
    growth_iq_is_active,
  } = user;

  // Redirected to KS if no Growth IQ access
  if (growth_iq !== 1 || user_growth_iq !== 1 || growth_iq_is_active !== 1) {
    window.location.replace(
      buildAuthenticatedReportUrl("https://www.mnmks.com")
    );
    removeTokens();
    return;
  }

  // Super Admin / Account Manager
  if (
    growth_iq_role_name === "ROLE_MNM_SUBSCRIBER" ||
    growth_iq_role_name === "ROLE_ACCOUNT_MANAGER"
  ) {
    return "/client_industry";
  }

  // Client Admin / Client Employee
  if (
    growth_iq_role_name === "ROLE_SUBSCRIBER_ADMIN" ||
    growth_iq_role_name === "ROLE_SUBSCRIBER_EMPLOYEE"
  ) {
    if (demo === APP_CONFIG?.market_iq_demo) {
      return "/markets";
    }

    if (has_ask_growth_access === 1) {
      return "/watchboard";
    }

    if (has_comp_iq_access === 1) {
      return "/clients_competitors";
    }

    if (salesplay === 1) {
      return "/customers";
    }

    return "/news";
  }

  // Logout if other than above cases
  handleLogout?.();
  // removeTokens();
  // return "/login";
};

export const hasData = (data) => {
  if (!data) return false; // handles null/undefined
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") return Object.keys(data).length > 0;
  return true;
};
